/**
 * Sprint 12+M5.17-t (22.07.2026) — Video-Indoctrination-Seite.
 * Sprint M5.39 (30.07.2026) — Block B1 Umbau:
 *   - Free-User: Drip-In 1 Video pro Tag, Tag-0 = Video für ermittelten Primärfokustyp.
 *   - Pro-User: Alle 5 Videos sofort verfügbar (kein Drip-In).
 *
 * Sprint V101.4 (Di 22.09.2026) — Single Source of Truth Umbau:
 *   Reihenfolge + Freischaltungs-Zeitpunkte kommen jetzt aus dem Backend
 *   (GET /api/video-drip/schedule), nicht mehr aus der frontend-lokalen
 *   alphabetischen Reihenfolge aus M5.39. Verhindert Inkonsistenz zwischen
 *   Dashboard-Drip-Tile (Nachbar-basiert) und Video-Grid (bisher alphab.).
 *
 * Route: /dashboard/videos (Free + Pro) und /dashboard/pionier/videos (Pionier).
 * Auth-Guard: Redirect zu /warteliste/one-click wenn kein User.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import RedesignLayout from "@/components/redesign/RedesignLayout";
import VimeoEmbed from "@/components/VimeoEmbed";
import { VIMEO_VIDEO_ORDER } from "@/config/vimeo";
import { useAuth } from "@/context/AuthContext";
import { isPremium } from "@/lib/licensing";

const API = process.env.REACT_APP_BACKEND_URL;
const FT_KEYS = ["katze", "eule", "biene", "hund"];

// Fallback-Reihenfolge nur genutzt wenn Backend-Schedule (noch) nicht geladen.
// Sprint V101.4: Nachbar-Reihenfolge statt alphabetisch, damit auch der
// Ladezustand konsistent mit dem Backend ist.
const DIAGONAL = { katze: "biene", biene: "katze", eule: "hund", hund: "eule" };
function fallbackDripOrder(primary) {
  if (!FT_KEYS.includes(primary)) return FT_KEYS.slice();
  const diag = DIAGONAL[primary];
  const neighbors = FT_KEYS.filter((k) => k !== primary && k !== diag).sort();
  return [primary, neighbors[0], neighbors[1], diag];
}

function buildOrder(primaryFokustyp) {
  const orderedKeys = fallbackDripOrder(primaryFokustyp);
  const intro = VIMEO_VIDEO_ORDER.find((v) => v.key === "intro");
  const byKey = VIMEO_VIDEO_ORDER.reduce((acc, v) => { acc[v.key] = v; return acc; }, {});
  const drips = orderedKeys.map((k) => byKey[k]).filter(Boolean);
  return { intro, drips };
}

export default function VideoIndoctrination() {
  const { user, loading } = useAuth() || {};
  const navigate = useNavigate();
  const isPro = isPremium(user);
  const primary = user?.primary_fokustyp || user?.fokustyp_primary || null;

  // Sprint V101.4 — Schedule vom Backend holen. Solange nicht geladen: Fallback.
  const [schedule, setSchedule] = useState(null);
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API}/api/video-drip/schedule`, { credentials: "include" });
        if (!r.ok) return;
        const data = await r.json();
        if (!cancelled) setSchedule(data);
      } catch (_e) { /* Fallback bleibt aktiv */ }
    })();
    return () => { cancelled = true; };
  }, [user]);

  // Wenn Schedule vom Backend da: Reihenfolge daraus ableiten.
  const { intro, drips, unlockAtByKey } = useMemo(() => {
    const byKey = VIMEO_VIDEO_ORDER.reduce((acc, v) => { acc[v.key] = v; return acc; }, {});
    const introVideo = VIMEO_VIDEO_ORDER.find((v) => v.key === "intro");
    if (schedule && Array.isArray(schedule.drips) && schedule.drips.length > 0) {
      const orderedVideos = schedule.drips.map((d) => byKey[d.key]).filter(Boolean);
      const unlockMap = {};
      schedule.drips.forEach((d) => { unlockMap[d.key] = d.unlock_at; });
      return { intro: introVideo, drips: orderedVideos, unlockAtByKey: unlockMap };
    }
    const fallback = buildOrder(primary);
    return { intro: fallback.intro, drips: fallback.drips, unlockAtByKey: {} };
  }, [schedule, primary]);

  if (loading) {
    return (
      <RedesignLayout>
        {() => (
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8 text-center text-slate-500 dark:text-slate-400" data-testid="video-indoctrination-loading">
            Lädt…
          </div>
        )}
      </RedesignLayout>
    );
  }

  if (!user) {
    return <Navigate to="/warteliste/one-click" replace />;
  }

  const signupDate = user.created_at || user.pionier_since || null;

  // Sprint M5.39: Drip-Offsets berechnen.
  const dripOffsetFor = (index) => (isPro ? 0 : index);

  return (
    <RedesignLayout>
      {() => (
        <div
          className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10"
          data-testid="video-indoctrination-page"
          data-tier={isPro ? "pro" : "free"}
        >
          <header className="mb-8 sm:mb-10">
            <p className="text-xs font-bold uppercase tracking-widest text-[var(--fts-primary-bright)]">
              Fokustypen — die Grundlagen
            </p>
            <h1
              className="mt-1 text-3xl sm:text-4xl font-extrabold text-slate-900 dark:text-white tracking-tight"
              data-testid="video-indoctrination-headline"
            >
              Deine 5 Videos zum Vier-Fokustypen-Modell
            </h1>
            {isPro ? (
              <p className="mt-2 text-base text-slate-600 dark:text-slate-300 max-w-2xl" data-testid="video-indoctrination-subline-pro">
                Als Pro-User sind alle Videos sofort verfügbar. Schau sie in deinem Tempo.
              </p>
            ) : (
              <p className="mt-2 text-base text-slate-600 dark:text-slate-300 max-w-2xl" data-testid="video-indoctrination-subline-free">
                {primary
                  ? `Dein erstes Video ist für deinen Primärfokustyp ${primary.charAt(0).toUpperCase()}${primary.slice(1)}.`
                  : "Mach zuerst den Quick-Check — dann startet dein erstes Video."}
                {" "}
                Komm zurück, um dir morgen das nächste Video anzuschauen.
              </p>
            )}
          </header>

          <div className="space-y-8 sm:space-y-10">
            {intro && (
              <section key={intro.key} data-testid={`video-section-${intro.key}`}>
                <div className="mb-3 sm:mb-4">
                  <h2 className="text-lg sm:text-xl font-bold text-slate-900 dark:text-white">
                    {intro.title}
                  </h2>
                  <p className="text-sm text-slate-600 dark:text-slate-300 mt-1">
                    {intro.description}
                    <span className="text-slate-400 dark:text-slate-500"> · {intro.duration}</span>
                  </p>
                </div>
                <VimeoEmbed videoKey={intro.key} userSignupDate={signupDate} overrideOffsetDays={0} />
              </section>
            )}

            {drips.map((video, i) => {
              const offset = dripOffsetFor(i);
              const isFirstAndPrimary = i === 0 && primary && video.key === primary;
              return (
                <section key={video.key} data-testid={`video-section-${video.key}`} data-drip-day={offset}>
                  <div className="mb-3 sm:mb-4">
                    <h2 className="text-lg sm:text-xl font-bold text-slate-900 dark:text-white">
                      {video.title}
                      {isFirstAndPrimary && !isPro && (
                        <span
                          data-testid={`video-badge-primary-${video.key}`}
                          className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest bg-[var(--fts-primary-bright)] text-white"
                        >
                          Dein Fokustyp
                        </span>
                      )}
                    </h2>
                    <p className="text-sm text-slate-600 dark:text-slate-300 mt-1">
                      {video.description}
                      <span className="text-slate-400 dark:text-slate-500"> · {video.duration}</span>
                    </p>
                  </div>
                  <VimeoEmbed
                    videoKey={video.key}
                    userSignupDate={signupDate}
                    overrideOffsetDays={offset}
                    unlockAt={unlockAtByKey[video.key]}
                  />
                </section>
              );
            })}
          </div>

          {!isPro && (
            <p
              className="mt-10 text-center text-sm text-slate-500 dark:text-slate-400 italic"
              data-testid="video-drip-reminder"
            >
              Komm zurück, um dir morgen das nächste Video anzuschauen.
            </p>
          )}
        </div>
      )}
    </RedesignLayout>
  );
}
